import { useInjectableServices } from '@src/server/service-registry.provider';
import { Context, createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TokenProviderContext } from '../auth/components/token-provider.component';

interface LocaleProviderContextValue {
  locale: string;
  updateLocale: (locale: string) => Promise<void>;
}
export const LocaleProviderContext: Context<LocaleProviderContextValue> = createContext(null);

export const CIAMLocaleProvider: FC = ({ children }) => {
  const { i18n } = useTranslation();
  const { LocaleService } = useInjectableServices();
  const { isFullyAuthenticated } = useContext(TokenProviderContext);

  const [locale, setLocale] = useState<string>(i18n.language);

  useEffect(() => {
    i18n.changeLanguage(locale);
  }, [locale, i18n]);

  const getInitialUserLocale = useCallback(async () => {
    const newLocale = await LocaleService.getLocale().then((response) => response.locale);
    setLocale(newLocale);

    return newLocale;
  }, [LocaleService]);

  useEffect(() => {
    if (isFullyAuthenticated) {
      getInitialUserLocale();
    }
  }, [isFullyAuthenticated, getInitialUserLocale]);

  const updateLocale = useCallback(
    async (newLocale: string) => {
      const previousLocale = locale;

      const promises: Promise<void>[] = [
        LocaleService.updateLocale(newLocale).catch(() => setLocale(previousLocale)),
        new Promise<void>(() => setLocale(newLocale)),
      ];

      await Promise.all(promises);
    },
    [LocaleService, locale]
  );

  return (
    <LocaleProviderContext.Provider
      value={{
        locale,
        updateLocale,
      }}
    >
      {children}
    </LocaleProviderContext.Provider>
  );
};

export const LocaleProvider: FC = ({ children }) => (
  <CIAMLocaleProvider>{children}</CIAMLocaleProvider>
);

export const useLocale = () => useContext(LocaleProviderContext);
